/**
 * /integration/connections/:connectionId — view + manage one connection.
 *
 * Test re-probes and refreshes health (FS-902). Rename and Rotate
 * credential both PATCH with the current `version` (optimistic lock,
 * FS-904) and capture an audit reason. Delete is admin-only (FS-905).
 * The credential is write-only — only `has_credential` is ever shown.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconFolderSearch, IconPlugConnected, IconTrash } from "@tabler/icons-react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { useMe } from "@/features/users/useMe";
import { classificationColor } from "@/shared/ui/palette";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import {
  authModeNeedsCredential,
  credentialLabel,
  deleteConnection,
  getConnection,
  hasBrowser,
  healthColor,
  s3Credential,
  testSavedConnection,
  updateConnection,
  type ConnectionOut,
} from "@/shared/api/integration";

export function ConnectionDetail() {
  const { connectionId = "" } = useParams();
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isAdmin = me?.roles?.includes("administrator") ?? false;

  const [conn, setConn] = useState<ConnectionOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [newCredential, setNewCredential] = useState("");
  // S3 rotates a *pair*: the new key id travels with the new secret in the one
  // JSON credential the connection stores (ADR-0021 §2 / ADR-0007).
  const [newAccessKeyId, setNewAccessKeyId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const c = await getConnection(token, connectionId);
      setConn(c);
      setRenameValue(c.display_name);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getToken, connectionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onTest = async () => {
    if (!conn) return;
    setBusy(true);
    try {
      const token = await getToken();
      const result = await testSavedConnection(token, conn.connection_id);
      if (result.ok) toastSuccess("Connection healthy", "The source responded OK.");
      else toastWarning("Connection failing", "The source could not be reached.");
      await load();
    } catch (err) {
      toastApiError("Test failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onRename = async () => {
    if (!conn || renameValue.trim().length === 0) return;
    const reason = await promptForReason("Rename connection", "Audit reason");
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await updateConnection(token, conn.connection_id, {
        expected_version: conn.version,
        reason,
        display_name: renameValue.trim(),
      });
      toastSuccess("Renamed", "The connection name was updated.");
      await load();
    } catch (err) {
      toastApiError("Rename failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onRotate = async () => {
    if (!conn || newCredential.length === 0) return;
    if (conn.source_type === "s3" && newAccessKeyId.trim().length === 0) return;
    const reason = await promptForReason("Rotate credential", "Audit reason", {
      confirmLabel: "Rotate",
    });
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await updateConnection(token, conn.connection_id, {
        expected_version: conn.version,
        reason,
        credential:
          conn.source_type === "s3" ? s3Credential(newAccessKeyId, newCredential) : newCredential,
      });
      setNewCredential("");
      setNewAccessKeyId("");
      toastSuccess("Credential rotated", "A new secret version was written to Key Vault.");
      await load();
    } catch (err) {
      toastApiError("Rotation failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!conn) return;
    const reason = await promptForReason(`Delete "${conn.display_name}"`, "Audit reason", {
      warning: "This removes the connection and disables its stored credential.",
      confirmLabel: "Delete",
    });
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await deleteConnection(token, conn.connection_id, reason);
      toastWarning("Connection deleted", `${conn.display_name} was removed.`);
      navigate("/integration/connections");
    } catch (err) {
      toastApiError("Delete failed", err);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
      </Group>
    );
  }
  if (error || !conn) {
    return (
      <Alert variant="light" color="red" title="Could not load connection">
        {error ?? "Not found."}
      </Alert>
    );
  }

  // What this connection's credential is called, lowercased for inline use.
  const credentialName = credentialLabel(conn.source_type, conn.auth_mode).toLowerCase();

  return (
    <Stack gap="md" maw={720}>
      <Group justify="space-between" align="center">
        <Title order={2}>{conn.display_name}</Title>
        <Group gap="xs">
          {/* Browsing lives in its own left-nav section now; this is a shortcut
              that opens it with this connection already picked. Only for source
              types with a browser — an AI connection has no data to browse. */}
          {hasBrowser(conn.source_type) && (
            <Button
              variant="light"
              leftSection={<IconFolderSearch size={16} />}
              component={RouterLink}
              to={`/browse/${conn.source_type}/${conn.connection_id}`}
            >
              Browse
            </Button>
          )}
          <Button
            variant="default"
            leftSection={<IconPlugConnected size={16} />}
            onClick={() => void onTest()}
            loading={busy}
          >
            Test
          </Button>
          {isAdmin && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconTrash size={16} />}
              onClick={() => void onDelete()}
              disabled={busy}
            >
              Delete
            </Button>
          )}
        </Group>
      </Group>

      <Card withBorder radius="md">
        <SimpleGrid cols={2} spacing="sm">
          <Field label="Source type" value={conn.source_type} />
          <Field label="Auth mode" value={conn.auth_mode} />
          <Field
            label="Classification"
            value={
              <Badge variant="outline" color={classificationColor(conn.classification)}>
                {conn.classification}
              </Badge>
            }
          />
          <Field
            label="Health"
            value={
              <Badge variant="light" color={healthColor(conn.health_status)}>
                {conn.health_status}
              </Badge>
            }
          />
          <Field label="Version" value={String(conn.version)} />
          <Field
            label="Credential stored"
            value={
              authModeNeedsCredential(conn.auth_mode)
                ? conn.has_credential
                  ? "Yes"
                  : "No"
                : "Not needed (managed identity)"
            }
          />
          <Field label="Last tested (UTC)" value={conn.last_tested_at_utc ?? "—"} />
        </SimpleGrid>
      </Card>

      <Card withBorder radius="md">
        <Stack gap="sm">
          <Text fw={600}>Rename</Text>
          <Group align="flex-end" gap="sm">
            <TextInput
              label="Display name"
              value={renameValue}
              onChange={(e) => setRenameValue(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              onClick={() => void onRename()}
              disabled={
                busy || renameValue.trim() === conn.display_name || renameValue.trim() === ""
              }
            >
              Rename
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Only for connections that HAVE a credential. A managed-identity
          connection has none — access is a role assignment — so offering to
          rotate one would invite writing a secret the auth mode never reads. */}
      {authModeNeedsCredential(conn.auth_mode) && (
        <Card withBorder radius="md">
          <Stack gap="sm">
            <Text fw={600}>Rotate {credentialName}</Text>
            <Text size="sm" c="dimmed">
              Writes a new secret version to Key Vault. The connection id is unchanged, so
              downstream consumers keep working.
            </Text>
            <Group align="flex-end" gap="sm">
              {/* S3 stores the key id alongside the secret, so rotating one
                  without the other would write half a credential. */}
              {conn.source_type === "s3" && (
                <TextInput
                  label="New access key ID"
                  placeholder="AKIA…"
                  value={newAccessKeyId}
                  onChange={(e) => setNewAccessKeyId(e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
              )}
              <PasswordInput
                label={`New ${credentialName}`}
                value={newCredential}
                onChange={(e) => setNewCredential(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Button
                onClick={() => void onRotate()}
                disabled={
                  busy ||
                  newCredential.length === 0 ||
                  (conn.source_type === "s3" && newAccessKeyId.trim().length === 0)
                }
              >
                Rotate
              </Button>
            </Group>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </Stack>
  );
}
